/**
 * CampaignQueueMonitor - Página de monitoramento da fila de campanhas
 * 
 * Disponível apenas para Admin Root
 * Permite diagnóstico detalhado de problemas em campanhas
 * Filtros por tenant, campanha, telefone, status, provider
 */

import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RefreshCw, Search, Phone, Megaphone } from 'lucide-react';
import { logger } from '@/lib/logger';
import { CampaignQueueStatusCards } from '@/components/dashboard/CampaignQueueStatusCards';
import { CampaignQueueVolumeChart } from '@/components/dashboard/CampaignQueueVolumeChart';
import { CampaignQueueHealthAlerts } from '@/components/dashboard/CampaignQueueHealthAlerts';
import { CampaignQueueItemsTable } from '@/components/dashboard/CampaignQueueItemsTable';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { 
  useCampaignQueueStats, 
  useCampaignQueueItems, 
  useCampaignQueueVolume, 
  useCampaignQueueAlerts 
} from '@/hooks/useCampaignQueueMonitor';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Página de monitoramento da fila de campanhas para Admin Root
 */
export default function CampaignQueueMonitor() {
  // Estados de filtro
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Buscar lista de tenants para dropdown
  const { data: tenants } = useQuery({
    queryKey: ['tenants-list-campaign'],
    queryFn: async () => {
      const { data } = await supabase
        .from('tenants')
        .select('id, name')
        .order('name');
      return data || [];
    }
  });

  // Buscar lista de campanhas (filtrado por tenant se selecionado)
  const { data: campaigns } = useQuery({
    queryKey: ['campaigns-list-admin', tenantFilter],
    queryFn: async () => {
      let query = supabase
        .from('campaigns')
        .select('id, name, tenant_id')
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (tenantFilter !== 'all') {
        query = query.eq('tenant_id', tenantFilter);
      }
      
      const { data } = await query;
      return data || [];
    }
  });

  // Buscar dados da fila
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useCampaignQueueStats();
  const { data: items, isLoading: itemsLoading, refetch: refetchItems } = useCampaignQueueItems(statusFilter, providerFilter, 500);
  const { data: volume, isLoading: volumeLoading } = useCampaignQueueVolume(24);
  const { data: alerts, isLoading: alertsLoading, refetch: refetchAlerts } = useCampaignQueueAlerts();

  // Filtrar e paginar itens no frontend
  const { paginatedItems, totalFiltered } = useMemo(() => {
    if (!items) return { paginatedItems: [], totalFiltered: 0 };
    
    let filtered = items;
    
    // Filtro por tenant
    if (tenantFilter !== 'all') {
      filtered = filtered.filter(item => item.tenant_id === tenantFilter);
    }
    
    // Filtro por campanha
    if (campaignFilter !== 'all') {
      filtered = filtered.filter(item => item.campaign_id === campaignFilter);
    }
    
    // Filtro por telefone
    if (phoneSearch.trim()) {
      const searchTerm = phoneSearch.replace(/\D/g, '');
      filtered = filtered.filter(item => {
        const phone = item.phone.replace(/\D/g, '');
        return phone.includes(searchTerm);
      });
    }
    
    // Paginar
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filtered.length);
    
    return {
      paginatedItems: filtered.slice(startIndex, endIndex),
      totalFiltered: filtered.length
    };
  }, [items, currentPage, pageSize, tenantFilter, campaignFilter, phoneSearch]);

  // Resetar página ao mudar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, providerFilter, tenantFilter, campaignFilter, phoneSearch]);

  // Resetar filtro de campanha ao mudar tenant
  useEffect(() => {
    setCampaignFilter('all');
  }, [tenantFilter]);

  // Estado de loading para refresh manual
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  // Função para refresh manual
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['campaign-queue-stats-admin'] }),
        queryClient.invalidateQueries({ queryKey: ['campaign-queue-items-admin'] }),
        queryClient.invalidateQueries({ queryKey: ['campaign-queue-volume-admin'] }),
        queryClient.invalidateQueries({ queryKey: ['campaign-queue-alerts-admin'] }),
      ]);
      setLastUpdate(new Date());
      toast.success('Dados atualizados');
    } catch (error) {
      logger.error('[CampaignQueueMonitor] Erro ao atualizar dados:', error);
      toast.error('Erro ao atualizar dados');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Megaphone className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">Fila de Campanhas</h1>
          </div>
          <p className="text-muted-foreground">
            Monitoramento de processamento de campanhas e diagnóstico de problemas
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Última atualização: {formatDistanceToNow(lastUpdate, { 
              addSuffix: true,
              locale: ptBR 
            })}
          </p>
        </div>
        <Button onClick={handleRefresh} disabled={isRefreshing}>
          <RefreshCw className={cn("mr-2 h-4 w-4", isRefreshing && "animate-spin")} />
          Atualizar
        </Button>
      </div>

      {/* Status Cards */}
      <CampaignQueueStatusCards stats={stats} isLoading={statsLoading} />

      {/* Alertas de Saúde */}
      <CampaignQueueHealthAlerts alerts={alerts} isLoading={alertsLoading} />

      {/* Gráfico de Volume */}
      <CampaignQueueVolumeChart data={volume} isLoading={volumeLoading} />

      {/* Filtros e Tabela */}
      <div className="space-y-4">
        {/* Linha de filtros */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Filtro Provider */}
          <Select value={providerFilter} onValueChange={setProviderFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Providers</SelectItem>
              <SelectItem value="z-api">Z-API</SelectItem>
              <SelectItem value="evolution">Evolution</SelectItem>
              <SelectItem value="wuzapi">WuzAPI</SelectItem>
              <SelectItem value="notificame">Notificame</SelectItem>
            </SelectContent>
          </Select>

          {/* Filtro Tenant */}
          <Select value={tenantFilter} onValueChange={setTenantFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Tenant" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Tenants</SelectItem>
              {tenants?.map((tenant) => (
                <SelectItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtro Campanha */}
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Campanha" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas Campanhas</SelectItem>
              {campaigns?.map((campaign) => (
                <SelectItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Campo de pesquisa por telefone */}
          <div className="relative">
            <Phone className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por telefone..."
              value={phoneSearch}
              onChange={(e) => setPhoneSearch(e.target.value)}
              className="w-[200px] pl-8"
            />
          </div>
        </div>

        {/* Tabela de Items */}
        <CampaignQueueItemsTable
          items={paginatedItems}
          isLoading={itemsLoading}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />

        {/* Paginação */}
        <PaginationFooter
          totalItems={totalFiltered}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[25, 50, 100, 200]}
        />
      </div>
    </div>
  );
}
